import React, { useState, useEffect } from 'react';
import './CourseRegistrationForm.css';

interface Program {
  id: string;
  name: string;
  programType: string;
  tier: string;
  description: string;
  durationMinutes: number;
  sessionsRequired: number;
  color: string;
  status: string;
}

interface Student {
  id: string;
  firstName: string;
  lastName: string;
  tier: 'foundations' | 'innovators' | 'pioneers';
}

interface Session {
  id: string;
  date?: string;
  date_available?: string;
  startTime?: string;
  start_time?: string;
  endTime?: string;
  end_time?: string;
  location?: string;
  maxCapacity?: number;
  max_capacity?: number;
  currentEnrollment?: number;
  current_enrollment?: number;
  scheduled_count?: number;
  available?: boolean;
}

interface CourseRegistrationFormProps {
  students: Student[];
  onClose: () => void;
  onSuccess: (registrationData: any) => void;
}

const CourseRegistrationForm: React.FC<CourseRegistrationFormProps> = ({
  students,
  onClose,
  onSuccess
}) => {
  // Form state
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(
    students.length === 1 ? students[0] : null
  );
  const [selectedProgram, setSelectedProgram] = useState<string>('');
  const [selectedTier, setSelectedTier] = useState<string>('');
  const [isPrivateGroup, setIsPrivateGroup] = useState(false);
  const [selectedMonths, setSelectedMonths] = useState<string[]>([]);
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [scheduleOption, setScheduleOption] = useState<'fixed' | 'custom'>('fixed');
  const [selectedSessions, setSelectedSessions] = useState<string[]>([]);
  
  // Data state
  const [programs, setPrograms] = useState<Program[]>([]);
  const [availableSessions, setAvailableSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Update tier when student is selected
  useEffect(() => {
    if (selectedStudent) {
      setSelectedTier(selectedStudent.tier);
    }
  }, [selectedStudent]);
  
  // Check if selected program is Private Group
  useEffect(() => {
    if (selectedProgram) {
      const program = programs.find(p => p.id === selectedProgram);
      setIsPrivateGroup(program?.name === 'Private Group' || program?.tier === 'all');
    } else {
      setIsPrivateGroup(false);
    }
  }, [selectedProgram, programs]);
  
  // Available months (current month + next 5 months)
  const getAvailableMonths = () => {
    const months = [];
    const currentDate = new Date();
    
    // Start from current month
    for (let i = 0; i < 6; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + i, 1);
      const year = date.getFullYear();
      const month = date.getMonth();
      const monthName = date.toLocaleDateString('en-US', { month: 'long' });
      const value = `${year}-${String(month + 1).padStart(2, '0')}`;
      const label = `${monthName} ${year}`;
      
      months.push({ value, label });
    }
    
    return months;
  };
  
  const availableMonths = getAvailableMonths();
  
  const daysOfWeek = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' }
  ];

  // Load programs on component mount and when student changes
  useEffect(() => {
    loadPrograms();
  }, [selectedStudent]);

  const loadPrograms = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/programs');
      if (response.ok) {
        const data = await response.json();
        const allPrograms = data.programs || [];
        
        console.log('🔍 All programs loaded:', allPrograms.length);
        console.log('🔍 Selected student:', selectedStudent);
        
        // Filter to only active programs first
        const activePrograms = allPrograms.filter((p: Program) => {
          const isActive = p.status === 'active' || !p.status; // Include active or undefined status
          console.log(`🔍 Program "${p.name}" status: ${p.status}, isActive: ${isActive}`);
          return isActive;
        });
        console.log('🔍 Active programs:', activePrograms.length, activePrograms.map((p: Program) => `${p.name} (tier: ${p.tier})`));
        
        // When a student is selected, filter to show only "Private Group" (tier="all")
        // and programs matching the student's tier
        if (selectedStudent) {
          const filteredPrograms = activePrograms.filter((p: Program) => {
            // Use case-insensitive comparison for tier
            const programTier = (p.tier || '').toLowerCase();
            const studentTier = (selectedStudent.tier || '').toLowerCase();
            // Show Private Group (tier="all") or programs matching student tier
            const matches = programTier === 'all' || programTier === studentTier;
            console.log(`🔍 Program "${p.name}" (tier: ${programTier}) matches student tier (${studentTier}): ${matches}`);
            return matches;
          });
          console.log('🔍 Filtered programs for student:', filteredPrograms.length, filteredPrograms.map((p: Program) => `${p.name} (tier: ${p.tier})`));
          setPrograms(filteredPrograms);
        } else {
          console.log('🔍 No student selected, showing all active programs');
          setPrograms(activePrograms);
        }
      }
    } catch (error) {
      console.error('Error loading programs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableSessions = async () => {
    // For Private Group, load all sessions for the program
    if (isPrivateGroup && selectedProgram) {
      try {
        setLoading(true);
        // Fetch all sessions for Private Group program
        const response = await fetch(`/api/class-sessions/?program_id=${selectedProgram}`);
        if (response.ok) {
          const data = await response.json();
          const sessions = Array.isArray(data) ? data : [];
          console.log('🔍 Loaded Private Group sessions:', sessions.length);
          // Filter to only show future sessions
          const now = new Date();
          const futureSessions = sessions.filter((s: Session) => {
            const dateStr = s.date || s.date_available;
            if (!dateStr) return true; // Include sessions without dates
            const sessionDate = new Date(dateStr);
            return sessionDate >= now;
          });
          setAvailableSessions(futureSessions);
        }
      } catch (error) {
        console.error('Error loading Private Group sessions:', error);
      } finally {
        setLoading(false);
      }
      return;
    }
    
    // For regular programs, use the existing logic
    if (!selectedProgram || selectedMonths.length === 0 || selectedDays.length === 0) {
      return;
    }

    try {
      setLoading(true);
      // This would be a new API endpoint to get available sessions based on criteria
      const response = await fetch(`/api/available-sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          programId: selectedProgram,
          months: selectedMonths.map(monthValue => {
            // Convert "2025-10" to "October 2025"
            const [year, month] = monthValue.split('-');
            const date = new Date(parseInt(year), parseInt(month) - 1, 1);
            const monthName = date.toLocaleDateString('en-US', { month: 'long' });
            return `${monthName} ${year}`;
          }),
          daysOfWeek: selectedDays,
          studentTier: selectedStudent!.tier
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        // API returns sessions directly as array, not wrapped in 'sessions' property
        const sessions = Array.isArray(data) ? data : [];
        console.log('🔍 Loaded sessions:', sessions.length, 'sessions for criteria:', {
          program: selectedProgram,
          months: selectedMonths,
          days: selectedDays
        });
        setAvailableSessions(sessions);
      }
    } catch (error) {
      console.error('Error loading sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load sessions when criteria changes
  useEffect(() => {
    if (isPrivateGroup && selectedProgram) {
      // For Private Group, load sessions immediately when program is selected
      loadAvailableSessions();
    } else if (selectedProgram && selectedMonths.length > 0 && selectedDays.length > 0) {
      // For regular programs, wait for months and days selection
      loadAvailableSessions();
    }
  }, [selectedProgram, selectedMonths, selectedDays, isPrivateGroup]);

  const handleMonthToggle = (month: string) => {
    setSelectedMonths(prev => {
      const newMonths = prev.includes(month) 
        ? prev.filter(m => m !== month)
        : [...prev, month];
      
      // Clear sessions when month selection changes
      if (newMonths.length !== prev.length) {
        console.log('🔄 Month selection changed, clearing sessions...');
        setAvailableSessions([]);
        setSelectedSessions([]);
        
        // Force reload sessions if we have all required criteria
        if (selectedProgram && newMonths.length > 0 && selectedDays.length > 0) {
          console.log('🔄 Forcing session reload after month change...');
          setTimeout(() => {
            loadAvailableSessions();
          }, 100);
        }
      }
      
      return newMonths;
    });
  };

  const handleDayToggle = (day: string) => {
    setSelectedDays(prev => {
      const newDays = prev.includes(day) 
        ? prev.filter(d => d !== day)
        : [...prev, day];
      
      // Clear sessions when day selection changes
      if (newDays.length !== prev.length) {
        console.log('🔄 Day selection changed, clearing sessions...');
        setAvailableSessions([]);
        setSelectedSessions([]);
        
        // Force reload sessions if we have all required criteria
        if (selectedProgram && selectedMonths.length > 0 && newDays.length > 0) {
          console.log('🔄 Forcing session reload after day change...');
          setTimeout(() => {
            loadAvailableSessions();
          }, 100);
        }
      }
      
      return newDays;
    });
  };

  const handleSessionToggle = (sessionId: string) => {
    setSelectedSessions(prev => 
      prev.includes(sessionId) 
        ? prev.filter(s => s !== sessionId)
        : [...prev, sessionId]
    );
  };

  const handleNext = () => {
    // For Private Group, skip steps 3-6 (tier, months, days, schedule) and go directly to session selection
    if (isPrivateGroup && currentStep === 2) {
      // Skip to step 7 (session selection)
      setCurrentStep(7);
      // Load sessions immediately
      loadAvailableSessions();
    } else if (currentStep < 7) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      
      const registrationData = {
        studentId: selectedStudent!.id,
        programId: selectedProgram,
        tier: isPrivateGroup ? selectedStudent!.tier : selectedTier,
        months: isPrivateGroup ? [] : selectedMonths.map(monthValue => {
          // Convert "2025-10" to "October 2025"
          const [year, month] = monthValue.split('-');
          const date = new Date(parseInt(year), parseInt(month) - 1, 1);
          const monthName = date.toLocaleDateString('en-US', { month: 'long' });
          return `${monthName} ${year}`;
        }),
        days: isPrivateGroup ? [] : selectedDays,
        scheduleOption: isPrivateGroup ? 'custom' : scheduleOption,
        selectedSessions,
        totalSessions: selectedSessions.length
      };

      // Submit registration
      const response = await fetch('/api/course-registrations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registrationData)
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Successfully registered for ${selectedSessions.length} session(s)!`);
        onSuccess(registrationData);
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Registration failed' }));
        throw new Error(errorData.detail || 'Registration failed');
      }
    } catch (error: any) {
      console.error('Error submitting registration:', error);
      alert(error.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return selectedStudent !== null;
      case 2: return selectedProgram !== '';
      case 3: return isPrivateGroup ? true : selectedTier !== ''; // Skip tier validation for Private Group
      case 4: return isPrivateGroup ? true : selectedMonths.length > 0; // Skip months for Private Group
      case 5: return isPrivateGroup ? true : selectedDays.length > 0; // Skip days for Private Group
      case 6: return true; // scheduleOption is always valid since it's initialized
      case 7: return selectedSessions.length > 0;
      default: return false;
    }
  };

  const formatSessionDate = (dateStr: string) => {
    if (!dateStr) return 'Date TBD';
    // Parse the date string to avoid timezone conversion issues
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatSessionTime = (startTime: string | undefined, endTime: string | undefined) => {
    if (!startTime || !endTime) return 'Time TBD';
    
    // Handle ISO format (e.g., "15:00:00" or "15:00:00.000000")
    const parseTime = (timeStr: string) => {
      // Remove timezone info if present and extract HH:MM
      const timePart = timeStr.split('T')[1]?.split('.')[0] || timeStr.split('.')[0];
      const [hours, minutes] = timePart.split(':');
      return { hours: parseInt(hours), minutes: parseInt(minutes) };
    };
    
    try {
      const start = parseTime(startTime);
      const end = parseTime(endTime);
      
      const formatTime = (h: number, m: number) => {
        const period = h >= 12 ? 'PM' : 'AM';
        const hour12 = h > 12 ? h - 12 : (h === 0 ? 12 : h);
        return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
      };
      
      return `${formatTime(start.hours, start.minutes)} to ${formatTime(end.hours, end.minutes)} PST`;
    } catch (error) {
      return `${startTime} to ${endTime}`;
    }
  };

  return (
    <div className="course-registration-modal">
      <div className="course-registration-content">
        <div className="modal-header">
          <h2>📚 Course Registration{selectedStudent ? ` - ${selectedStudent.firstName} ${selectedStudent.lastName}` : ''}</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Progress Indicator */}
        <div className="progress-indicator">
          {[1, 2, 3, 4, 5, 6, 7].map(step => (
            <div 
              key={step} 
              className={`progress-step ${currentStep >= step ? 'active' : ''}`}
            >
              {step}
            </div>
          ))}
        </div>

        {/* Step 1: Select Student */}
        {currentStep === 1 && (
          <div className="registration-step">
            <h3>1. Select Student</h3>
            <p>Choose which child to register for the program</p>
            
            <div className="student-options">
              {students.map(student => (
                <div 
                  key={student.id} 
                  className={`student-card ${selectedStudent?.id === student.id ? 'selected' : ''}`}
                  onClick={() => setSelectedStudent(student)}
                >
                  <div className="student-header">
                    <h4>{student.firstName} {student.lastName}</h4>
                    <span className="student-tier">{student.tier}</span>
                  </div>
                  <p className="student-info">Tier: {student.tier}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Select Program */}
        {currentStep === 2 && (
          <div className="registration-step">
            <h3>2. Select Program</h3>
            <p>Choose the program type for {selectedStudent?.firstName}</p>
            {isPrivateGroup && selectedProgram && (
              <div className="info-banner" style={{ padding: '10px', backgroundColor: '#e3f2fd', borderRadius: '4px', marginBottom: '15px' }}>
                <strong>Private Group Selected:</strong> You can now select specific sessions to register for.
              </div>
            )}
            
            <div className="program-options">
              {programs.map(program => (
                <div 
                  key={program.id} 
                  className={`program-card ${selectedProgram === program.id ? 'selected' : ''}`}
                  onClick={() => setSelectedProgram(program.id)}
                >
                  <div className="program-header">
                    <h4>{program.name}</h4>
                    <span className="program-type">{program.programType}</span>
                  </div>
                  <p className="program-description">{program.description}</p>
                  <div className="program-details">
                    <span>Duration: {program.durationMinutes} minutes</span>
                    <span>Sessions: {program.sessionsRequired}</span>
                    <span>Tier: {program.tier}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Select Tier */}
        {currentStep === 3 && (
          <div className="registration-step">
            <h3>3. Select Tier</h3>
            <p>Student tier (pre-populated from student profile)</p>
            
            <div className="tier-options">
              {['foundations', 'innovators', 'pioneers'].map(tier => (
                <div 
                  key={tier} 
                  className={`tier-card ${selectedTier === tier ? 'selected' : ''}`}
                  onClick={() => setSelectedTier(tier)}
                >
                  <h4>
                    {tier === 'foundations' && '🏗️ Foundations'}
                    {tier === 'innovators' && '🚀 Innovators'}
                    {tier === 'pioneers' && '🌟 Pioneers'}
                  </h4>
                  <p>
                    {tier === 'foundations' && 'Ages 6-9 • Basic robotics, programming, and STEM concepts'}
                    {tier === 'innovators' && 'Ages 10-13 • Advanced programming, robotics, and engineering'}
                    {tier === 'pioneers' && 'Ages 14+ • Advanced AI, machine learning, and research projects'}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 4: Select Registration Period */}
        {currentStep === 4 && (
          <div className="registration-step">
            <h3>4. Select Registration Period</h3>
            <p>Which month(s) do you want to register for?</p>
            
            <div className="month-options">
              {availableMonths.map(month => (
                <label key={month.value} className="month-option">
                  <input
                    type="checkbox"
                    checked={selectedMonths.includes(month.value)}
                    onChange={() => handleMonthToggle(month.value)}
                  />
                  <span className="checkmark"></span>
                  {month.label}
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Step 5: Select Days of Week */}
        {currentStep === 5 && (
          <div className="registration-step">
            <h3>5. Select Days of Week</h3>
            <p>Which day(s) of the week?</p>
            
            <div className="day-options">
              {daysOfWeek.map(day => (
                <label key={day.value} className="day-option">
                  <input
                    type="checkbox"
                    checked={selectedDays.includes(day.value)}
                    onChange={() => handleDayToggle(day.value)}
                  />
                  <span className="checkmark"></span>
                  {day.label}
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Step 6: Select Schedule Option */}
        {currentStep === 6 && (
          <div className="registration-step">
            <h3>6. Select Schedule Option</h3>
            <p>Choose your preferred scheduling method</p>
            
            <div className="schedule-options">
              <label className={`schedule-option ${scheduleOption === 'fixed' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="scheduleOption"
                  value="fixed"
                  checked={scheduleOption === 'fixed'}
                  onChange={(e) => setScheduleOption(e.target.value as 'fixed')}
                />
                <div className="schedule-card">
                  <h4>📅 Fixed Schedule</h4>
                  <p>Use the standard time slots for this program</p>
                </div>
              </label>
              
              <label className={`schedule-option ${scheduleOption === 'custom' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="scheduleOption"
                  value="custom"
                  checked={scheduleOption === 'custom'}
                  onChange={(e) => setScheduleOption(e.target.value as 'custom')}
                />
                <div className="schedule-card">
                  <h4>🎯 Custom Schedule</h4>
                  <p>Select specific days and times from available options</p>
                </div>
              </label>
            </div>
          </div>
        )}

        {/* Step 7: Review & Select Specific Sessions */}
        {currentStep === 7 && (
          <div className="registration-step">
            <h3>7. Review & Select Specific Sessions</h3>
            <p>Review your sessions for the selected program</p>
            
            {loading ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Loading available sessions...</p>
              </div>
            ) : (
              <div className="sessions-review">
                {availableSessions.length === 0 ? (
                  <div className="no-sessions">
                    <p>No sessions available for the selected criteria.</p>
                    <p>Please adjust your selection and try again.</p>
                  </div>
                ) : (
                  <div className="sessions-list">
                    {availableSessions.map(session => {
                      const sessionDate = session.date || session.date_available || '';
                      const startTime = session.startTime || session.start_time;
                      const endTime = session.endTime || session.end_time;
                      const enrollment = session.scheduled_count || session.currentEnrollment || session.current_enrollment || 0;
                      const capacity = session.maxCapacity || session.max_capacity || 'N/A';
                      
                      return (
                        <label key={session.id} className="session-option">
                          <input
                            type="checkbox"
                            checked={selectedSessions.includes(session.id)}
                            onChange={() => handleSessionToggle(session.id)}
                          />
                          <span className="checkmark"></span>
                          <div className="session-details">
                            <div className="session-date">
                              {formatSessionDate(sessionDate)}
                            </div>
                            <div className="session-time">
                              {formatSessionTime(startTime, endTime)}
                            </div>
                            {session.location && (
                              <div className="session-location">
                                📍 {session.location}
                              </div>
                            )}
                            <div className="session-availability">
                              {enrollment}/{capacity} spots
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
                
                {selectedSessions.length > 0 && (
                  <div className="session-summary">
                    <h4>Total Sessions Selected: {selectedSessions.length} sessions</h4>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Navigation */}
        <div className="registration-navigation">
          {currentStep > 1 && (
            <button 
              className="btn btn-secondary" 
              onClick={() => {
                // For Private Group, if we're on step 7, go back to step 2
                if (isPrivateGroup && currentStep === 7) {
                  setCurrentStep(2);
                } else {
                  handlePrevious();
                }
              }}
            >
              ← Previous
            </button>
          )}
          
          {currentStep < 7 ? (
            <button 
              className="btn btn-primary" 
              onClick={handleNext}
              disabled={!canProceed()}
            >
              {isPrivateGroup && currentStep === 2 ? 'Select Sessions →' : 'Next →'}
            </button>
          ) : (
            <button 
              className="btn btn-success" 
              onClick={handleSubmit}
              disabled={!canProceed() || loading}
            >
              {loading ? '⏳ Registering...' : isPrivateGroup ? '✅ Register for Sessions' : '✅ Complete Registration'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseRegistrationForm;
